/**
 * The inventory: what a player owns, what is equipped, and the two operations
 * that move items between those states.
 *
 * ===========================================================================
 * EVERY FUNCTION HERE IS PURE
 * ===========================================================================
 *
 * Nothing mutates the profile it is given; each returns a new one. That is not
 * decoration — the garage previews a change before it is committed, the shop
 * needs "what would this cost me" without spending anything, and an undo of a
 * mis-click is a reference to the previous object rather than an inverse
 * operation nobody wrote. `cloneProfile` is the one place the copy happens.
 *
 * ===========================================================================
 * A REFUSED OPERATION RETURNS THE PROFILE UNCHANGED
 * ===========================================================================
 *
 * `equip` with an instance the player does not own, `buildBlueprint` with too
 * few Credits, `grantItem` with an id no longer in the catalogue: all return
 * the input profile, identical by reference. They do not throw, because every
 * one of them is reachable from a stored profile that has outlived a catalogue
 * change, and a garage that will not open is worse than a slot that will not
 * fill. `tradeIn` is the exception and throws, because it is destructive and a
 * silent no-op there would look exactly like a successful trade; callers that
 * want to ASK first have `tradeInProblem`, which the UI uses to grey the button
 * out and say why.
 */

import type { Rng } from '../physics/rng';
import {
  TRADE_IN_COUNT,
  type Certification,
  type CosmeticSlot,
  type InventoryItem,
  type ItemDef,
  type Loadout,
  type MatchStats,
  type PaintColor,
  type PlayerProfile,
  type Preset,
  type Rarity,
} from './cosmetics';
import {
  blueprintCost,
  rollTradeIn,
  TRADEABLE_RARITIES,
  type BlueprintRoll,
  type DropRoll,
} from './drops';
import { itemById } from './items';

// ---------------------------------------------------------------------------
// Default loadouts and presets
// ---------------------------------------------------------------------------

/**
 * Team colours as '#rrggbb'. The numbers are `TEAM_COLORS` in
 * src/render/materials.ts, written out rather than imported: that module pulls
 * in three.js, and this one has to import cleanly in Node. A test pins the two
 * against each other so the copy cannot drift.
 */
export const DEFAULT_TEAM_PRIMARY: readonly [string, string] = ['#2a7dff', '#ff8a1f'];
export const DEFAULT_TEAM_ACCENT: readonly [string, string] = ['#1d4fa8', '#a8571a'];

/** Nothing equipped, team colours as the arena paints them. */
export const defaultLoadout = (team: 0 | 1): Loadout => ({
  slots: {},
  primaryColor: DEFAULT_TEAM_PRIMARY[team],
  accentColor: DEFAULT_TEAM_ACCENT[team],
});

export const defaultPreset = (id = 'preset-1', name = 'Default'): Preset => ({
  id,
  name,
  blue: defaultLoadout(0),
  orange: defaultLoadout(1),
});

/** More than this and the preset row stops fitting on one line of the garage. */
export const MAX_PRESETS = 6;

// ---------------------------------------------------------------------------
// Cloning
// ---------------------------------------------------------------------------

const cloneLoadout = (l: Loadout): Loadout => ({
  slots: { ...l.slots },
  primaryColor: l.primaryColor,
  accentColor: l.accentColor,
});

const clonePreset = (p: Preset): Preset => ({
  id: p.id,
  name: p.name,
  blue: cloneLoadout(p.blue),
  orange: cloneLoadout(p.orange),
});

/**
 * A copy deep enough that no caller can reach a shared array or a shared
 * `certValue`. `InventoryItem.certValue` is the only mutable field in the whole
 * model and it is exactly the one a shallow copy would alias.
 */
export const cloneProfile = (p: PlayerProfile): PlayerProfile => ({
  version: p.version,
  displayName: p.displayName,
  wallet: { ...p.wallet },
  progress: { ...p.progress, stats: { ...p.progress.stats } },
  inventory: p.inventory.map(i => ({ ...i })),
  blueprints: p.blueprints.map(b => ({ ...b })),
  presets: p.presets.map(clonePreset),
  activePresetId: p.activePresetId,
  challenges: p.challenges.map(c => ({ ...c })),
  lastShopDay: p.lastShopDay,
});

// ---------------------------------------------------------------------------
// Presets and equipping
// ---------------------------------------------------------------------------

/** The preset in use, or the first one, or a fresh default. Never null. */
export const activePreset = (p: PlayerProfile): Preset =>
  p.presets.find(x => x.id === p.activePresetId) ?? p.presets[0] ?? defaultPreset();

export const activeLoadout = (p: PlayerProfile, team: 0 | 1): Loadout =>
  team === 0 ? activePreset(p).blue : activePreset(p).orange;

/**
 * The definition and the owned copy in a slot, or null when the slot is empty,
 * holds an instance that has since been traded away, or names an item no longer
 * in the catalogue. All three are the same thing to a renderer: draw nothing.
 */
export const resolvedItem = (
  p: PlayerProfile,
  team: 0 | 1,
  slot: CosmeticSlot
): { def: ItemDef; inv: InventoryItem } | null => {
  const instanceId = activeLoadout(p, team).slots[slot];
  if (instanceId === null || instanceId === undefined) return null;
  const inv = p.inventory.find(i => i.instanceId === instanceId);
  if (inv === undefined) return null;
  const def = itemById(inv.itemId);
  if (def === undefined) return null;
  return { def, inv };
};

const withSlot = (l: Loadout, slot: CosmeticSlot, instanceId: string | null): Loadout => ({
  ...cloneLoadout(l),
  slots: { ...l.slots, [slot]: instanceId },
});

const mapActivePreset = (p: PlayerProfile, fn: (preset: Preset) => Preset): PlayerProfile => {
  const next = cloneProfile(p);
  if (next.presets.length === 0) next.presets.push(defaultPreset());
  const index = Math.max(
    0,
    next.presets.findIndex(x => x.id === next.activePresetId)
  );
  const target = next.presets[index];
  if (target === undefined) return next;
  next.presets[index] = fn(target);
  next.activePresetId = next.presets[index].id;
  return next;
};

/**
 * Put an owned instance in a slot for one team colour, or clear it with null.
 *
 * Refuses — by returning the profile unchanged — an instance the player does
 * not own and an instance whose item belongs to a different slot. It does NOT
 * check `bodies`: which body is equipped is itself a slot, so enforcing the
 * restriction here would make the order of two legal equips decide whether the
 * second one works. The garage filters its list with `itemsForSlot(slot, body)`
 * instead, and `loadoutConflicts` reports a combination that has gone stale.
 */
export const equip = (
  p: PlayerProfile,
  team: 0 | 1,
  slot: CosmeticSlot,
  instanceId: string | null
): PlayerProfile => {
  if (instanceId !== null) {
    const inv = p.inventory.find(i => i.instanceId === instanceId);
    if (inv === undefined) return p;
    const def = itemById(inv.itemId);
    if (def === undefined || def.slot !== slot) return p;
  }
  return mapActivePreset(p, preset => ({
    ...preset,
    blue: team === 0 ? withSlot(preset.blue, slot, instanceId) : preset.blue,
    orange: team === 1 ? withSlot(preset.orange, slot, instanceId) : preset.orange,
  }));
};

/** Clear a slot. `equip(..., null)` spelled out, because that reads badly. */
export const unequip = (p: PlayerProfile, team: 0 | 1, slot: CosmeticSlot): PlayerProfile =>
  equip(p, team, slot, null);

/** Primary and accent for one team colour. Values are '#rrggbb'. */
export const setLoadoutColors = (
  p: PlayerProfile,
  team: 0 | 1,
  primaryColor: string,
  accentColor: string
): PlayerProfile =>
  mapActivePreset(p, preset => {
    const updated: Loadout = { ...cloneLoadout(team === 0 ? preset.blue : preset.orange), primaryColor, accentColor };
    return {
      ...preset,
      blue: team === 0 ? updated : preset.blue,
      orange: team === 1 ? updated : preset.orange,
    };
  });

/**
 * Every instance equipped anywhere — both teams, every preset. This is the set
 * `tradeIn` refuses to consume, so it deliberately spans presets: destroying an
 * item that is equipped in a preset you are not currently looking at is the
 * same surprise as destroying one you are.
 */
export const equippedInstanceIds = (p: PlayerProfile): ReadonlySet<string> => {
  const out = new Set<string>();
  for (const preset of p.presets) {
    for (const loadout of [preset.blue, preset.orange]) {
      for (const id of Object.values(loadout.slots)) {
        if (typeof id === 'string') out.add(id);
      }
    }
  }
  return out;
};

/**
 * Slots whose equipped item cannot be worn by the equipped body — the stale
 * combination `equip` deliberately does not prevent. The garage shows these as
 * a warning rather than silently swapping them out.
 */
export const loadoutConflicts = (p: PlayerProfile, team: 0 | 1): readonly CosmeticSlot[] => {
  const bodyItem = resolvedItem(p, team, 'body');
  const body = bodyItem?.def.bodies?.[0];
  if (body === undefined) return [];
  const out: CosmeticSlot[] = [];
  for (const [slot, instanceId] of Object.entries(activeLoadout(p, team).slots)) {
    if (typeof instanceId !== 'string' || slot === 'body') continue;
    const inv = p.inventory.find(i => i.instanceId === instanceId);
    const def = inv ? itemById(inv.itemId) : undefined;
    if (def?.bodies !== undefined && !def.bodies.includes(body)) out.push(slot as CosmeticSlot);
  }
  return out;
};

export const addPreset = (p: PlayerProfile, name?: string): PlayerProfile => {
  if (p.presets.length >= MAX_PRESETS) return p;
  const next = cloneProfile(p);
  let n = next.presets.length + 1;
  while (next.presets.some(x => x.id === `preset-${n}`)) n++;
  const preset = defaultPreset(`preset-${n}`, name ?? `Preset ${n}`);
  next.presets.push(preset);
  next.activePresetId = preset.id;
  return next;
};

export const selectPreset = (p: PlayerProfile, id: string): PlayerProfile => {
  if (!p.presets.some(x => x.id === id)) return p;
  const next = cloneProfile(p);
  next.activePresetId = id;
  return next;
};

export const renamePreset = (p: PlayerProfile, id: string, name: string): PlayerProfile => {
  if (!p.presets.some(x => x.id === id)) return p;
  const next = cloneProfile(p);
  for (const preset of next.presets) if (preset.id === id) preset.name = name;
  return next;
};

/** The last preset cannot be deleted: a profile with no preset has no loadout. */
export const deletePreset = (p: PlayerProfile, id: string): PlayerProfile => {
  if (p.presets.length <= 1 || !p.presets.some(x => x.id === id)) return p;
  const next = cloneProfile(p);
  next.presets = next.presets.filter(x => x.id !== id);
  if (next.activePresetId === id) next.activePresetId = next.presets[0]?.id ?? '';
  return next;
};

// ---------------------------------------------------------------------------
// Granting
// ---------------------------------------------------------------------------

const INSTANCE_PATTERN = /^c(\d+)$/;

/**
 * The next free instance id, derived from the profile rather than from a
 * counter or a clock. Both alternatives are worse for the same reason: an id
 * has to stay unique across a save, a reload, and an import of somebody else's
 * profile, and only the profile itself knows what it already contains.
 */
export const nextInstanceId = (p: PlayerProfile): string => {
  let max = 0;
  for (const id of [
    ...p.inventory.map(i => i.instanceId),
    ...p.blueprints.map(b => b.instanceId),
  ]) {
    const m = INSTANCE_PATTERN.exec(id);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `c${max + 1}`;
};

/**
 * Strictly increasing, and a real timestamp when the clock allows one. Two
 * items granted in the same millisecond must still sort in the order they
 * arrived, so the sequence never repeats a value it has already used.
 */
const nextAcquiredAt = (p: PlayerProfile): number => {
  let max = 0;
  for (const i of p.inventory) max = Math.max(max, i.acquiredAt);
  for (const b of p.blueprints) max = Math.max(max, b.acquiredAt);
  return Math.max(Date.now(), max + 1);
};

export interface GrantOptions {
  paint?: PaintColor;
  certification?: Certification;
  /** Override the timestamp. Tests use it; nothing in the game needs to. */
  acquiredAt?: number;
}

/**
 * Add one copy of a catalogue item.
 *
 * A paint on an unpaintable item, or a certification on an uncertifiable one,
 * is DROPPED rather than refused: the attribute tables and the catalogue can
 * both change, and an old save asking for a painted Common should lose the
 * paint, not the item.
 */
export const grantItem = (
  p: PlayerProfile,
  itemId: string,
  opts: GrantOptions = {}
): PlayerProfile => {
  const def = itemById(itemId);
  if (def === undefined) return p;
  const next = cloneProfile(p);
  next.inventory.push(mintItem(next, def, opts));
  return next;
};

/** Mints into `p` in place. Internal: callers below have already cloned. */
const mintItem = (p: PlayerProfile, def: ItemDef, opts: GrantOptions): InventoryItem => {
  const paint = def.paintable ? opts.paint : undefined;
  const certification = def.certifiable ? opts.certification : undefined;
  return {
    instanceId: nextInstanceId(p),
    itemId: def.id,
    ...(paint ? { paint } : {}),
    ...(certification ? { certification, certValue: 0 } : {}),
    acquiredAt: opts.acquiredAt ?? nextAcquiredAt(p),
  };
};

/**
 * Grant several rolls at once and report exactly what was added. One clone and
 * one id sequence for the batch, so a level-up that drops three items produces
 * c1, c2, c3 rather than three copies of c1.
 */
export const grantDrops = (
  p: PlayerProfile,
  rolls: readonly DropRoll[]
): { profile: PlayerProfile; granted: InventoryItem[] } => {
  const next = cloneProfile(p);
  const granted: InventoryItem[] = [];
  for (const roll of rolls) {
    const def = itemById(roll.itemId);
    if (def === undefined) continue;
    const item = mintItem(next, def, { paint: roll.paint, certification: roll.certification });
    next.inventory.push(item);
    granted.push(item);
  }
  return { profile: next, granted };
};

/**
 * Does the player own this item? With `paint` given, own it in that colour
 * specifically; without, own any copy at all — which is what a shop needs, so
 * it can grey out something already owned.
 */
export const ownsItem = (p: PlayerProfile, itemId: string, paint?: PaintColor): boolean =>
  p.inventory.some(i => i.itemId === itemId && (paint === undefined || i.paint === paint));

/** Every owned copy of an item, newest last. */
export const copiesOf = (p: PlayerProfile, itemId: string): readonly InventoryItem[] =>
  p.inventory.filter(i => i.itemId === itemId);

// ---------------------------------------------------------------------------
// Blueprints
// ---------------------------------------------------------------------------

export const grantBlueprint = (p: PlayerProfile, roll: BlueprintRoll): PlayerProfile => {
  const def = itemById(roll.itemId);
  if (def === undefined) return p;
  const next = cloneProfile(p);
  next.blueprints.push({
    instanceId: nextInstanceId(next),
    itemId: roll.itemId,
    ...(roll.paint ? { paint: roll.paint } : {}),
    cost: roll.cost > 0 ? roll.cost : blueprintCost(def, roll.paint),
    acquiredAt: nextAcquiredAt(next),
  });
  return next;
};

/**
 * Spend Credits to turn a blueprint into the item. Unchanged when the blueprint
 * is unknown or the wallet is short — the shop reads `wallet.credits` against
 * `blueprint.cost` to decide whether the button is live.
 */
export const buildBlueprint = (p: PlayerProfile, instanceId: string): PlayerProfile => {
  const bp = p.blueprints.find(b => b.instanceId === instanceId);
  if (bp === undefined) return p;
  if (p.wallet.credits < bp.cost) return p;
  const def = itemById(bp.itemId);
  if (def === undefined) return p;
  const next = cloneProfile(p);
  next.wallet.credits -= bp.cost;
  // Minted before the blueprint is dropped, for the reason `tradeIn` gives.
  const item = mintItem(next, def, { paint: bp.paint });
  next.blueprints = next.blueprints.filter(b => b.instanceId !== instanceId);
  next.inventory.push(item);
  return next;
};

// ---------------------------------------------------------------------------
// Trade-in
// ---------------------------------------------------------------------------

/**
 * Why this set cannot be traded in, or null if it can.
 *
 * THE EQUIPPED RULE. An equipped item is refused outright rather than being
 * unequipped for you. Trade-in destroys five items to make one, and quietly
 * stripping a car of its wheels on the way is a change the player did not ask
 * for and cannot see happen — the garage says "unequip it first", which is one
 * extra click and no surprise. `equippedInstanceIds` spans every preset, not
 * just the active one.
 */
export const tradeInProblem = (
  p: PlayerProfile,
  instanceIds: readonly string[]
): string | null => {
  if (instanceIds.length !== TRADE_IN_COUNT) {
    return `trade-in takes exactly ${TRADE_IN_COUNT} items`;
  }
  if (new Set(instanceIds).size !== instanceIds.length) {
    return 'the same item cannot be traded in twice';
  }
  const equipped = equippedInstanceIds(p);
  const rarities = new Set<Rarity>();
  for (const id of instanceIds) {
    const inv = p.inventory.find(i => i.instanceId === id);
    if (inv === undefined) return `item ${id} is not in the inventory`;
    if (equipped.has(id)) return `item ${id} is equipped — unequip it first`;
    const def = itemById(inv.itemId);
    if (def === undefined) return `item ${id} is not in the catalogue`;
    rarities.add(def.rarity);
  }
  if (rarities.size !== 1) return 'every item must be the same rarity';
  const rarity = [...rarities][0];
  if (rarity === undefined || !TRADEABLE_RARITIES.includes(rarity)) {
    return `${String(rarity)} is the top of the ladder and cannot be traded up`;
  }
  return null;
};

export const canTradeIn = (p: PlayerProfile, instanceIds: readonly string[]): boolean =>
  tradeInProblem(p, instanceIds) === null;

/**
 * Five of one rarity in, one of the next out. THROWS on an invalid set — see
 * the header for why this one operation does; `tradeInProblem` is the version
 * that asks.
 */
export const tradeIn = (
  p: PlayerProfile,
  instanceIds: readonly string[],
  rng: Rng
): { profile: PlayerProfile; received: InventoryItem } => {
  const problem = tradeInProblem(p, instanceIds);
  if (problem !== null) throw new Error(`trade-in refused: ${problem}`);

  const first = p.inventory.find(i => i.instanceId === instanceIds[0]);
  const rarity = first ? itemById(first.itemId)?.rarity : undefined;
  if (rarity === undefined) throw new Error('trade-in refused: unknown rarity');

  const roll = rollTradeIn(rng, rarity);
  if (roll === null) throw new Error(`trade-in refused: nothing to give for ${rarity}`);
  const def = itemById(roll.itemId);
  if (def === undefined) throw new Error('trade-in refused: rolled an unknown item');

  const next = cloneProfile(p);
  // Minted BEFORE the five are removed, so the new copy cannot be handed the
  // instance id of one of the items it was made from — `nextInstanceId` reads
  // the profile, and a shorter profile has a lower high-water mark.
  const received = mintItem(next, def, {
    paint: roll.paint,
    certification: roll.certification,
  });
  const consumed = new Set(instanceIds);
  next.inventory = next.inventory.filter(i => !consumed.has(i.instanceId));
  next.inventory.push(received);
  return { profile: next, received };
};

// ---------------------------------------------------------------------------
// Certifications
// ---------------------------------------------------------------------------

/**
 * What each certification counts, as a key of `MatchStats`.
 *
 * `Certification` is copied from Rocket League and Rocket League tracks things
 * this simulation does not — epic saves, centres, juggles, clears, turtle
 * touches. Rather than invent a statistic that nothing produces (which
 * ./cosmetics explicitly forbids), each of those maps to the nearest thing we
 * genuinely count, and every substitution is listed in
 * docs/COSMETICS_SOURCES.md. Two certifications sharing a key is fine; a
 * certification pointing at a counter that never moves is not.
 */
export const CERT_STAT: Readonly<Record<Certification, keyof MatchStats>> = Object.freeze({
  striker: 'goals',
  scorer: 'shots',
  sniper: 'longGoals',
  goalkeeper: 'saves',
  // Real: epic saves. We do not grade a save, so both keeper certs count saves.
  guardian: 'saves',
  playmaker: 'assists',
  // Real: centres. Touches is the nearest thing the world reports.
  tactician: 'touches',
  acrobat: 'aerialGoals',
  // Real: flair. A backwards goal is the flair we can actually detect.
  showOff: 'backwardsGoals',
  // Real: juggles.
  juggler: 'touches',
  // Real: clears.
  sweeper: 'demolitions',
  // Real: touches while upside down. Boost pickups is a real counter and a
  // positive one; own goals, the other unused key, is not something to track.
  turtle: 'boostCollected',
  aviator: 'aerialGoals',
  paragon: 'mvps',
  victor: 'wins',
});

/**
 * Advance the counter on every certified item that is equipped anywhere, by
 * what this match produced. Deduped by instance: an item equipped for both team
 * colours is one item and counts once.
 */
export const advanceCertifications = (
  p: PlayerProfile,
  stats: MatchStats
): PlayerProfile => {
  const equipped = equippedInstanceIds(p);
  if (equipped.size === 0) return p;
  const next = cloneProfile(p);
  for (const inv of next.inventory) {
    if (inv.certification === undefined || !equipped.has(inv.instanceId)) continue;
    const key = CERT_STAT[inv.certification];
    const gained = stats[key];
    if (typeof gained !== 'number' || gained <= 0) continue;
    inv.certValue = (inv.certValue ?? 0) + gained;
  }
  return next;
};
